import * as angular from "angular";
import { CountryDataProvider } from "../../../data-provider/country.dp";
import { ApiUserService } from "../../../services/user.service";

let InvitePhoneNumber = {
    selector: "vcInvitePhoneNumber",
    templateUrl:
        "/angular/components/invite/sections/invite-phone-number.component.html",
    bindings: {
        user: "=",
        finishCallback: "&",
        editing: "<?",
    },
    controller: class InvitePhoneNumberController {
        // * bindings for typescript compiler *
        public user: any;
        public finishCallback: Function;
        public editing?: boolean;
        // ************************************

        private apiUserService: ApiUserService;
        private readonly $mdConstant: any;
        private readonly $element: any;

        public countries: object[];
        public countryDataProvider: CountryDataProvider;
        public error = "";
        public errorMessage = "";
        public userInviteForm: any;

        // The number typed but not yet added, and inline-edit state.
        public draftNumber: string = "";
        public editingIndex: number = -1;
        public editValue: string = "";

        constructor(
            ApiUserService: ApiUserService,
            CountryDataProvider: CountryDataProvider,
            $mdConstant: any,
            $element: any
        ) {
            this.apiUserService = ApiUserService;
            this.$mdConstant = $mdConstant;
            this.$element = $element;

            this.countryDataProvider = CountryDataProvider;
            this.countries = this.countryDataProvider.getCountries();
        }

        // Bindings (`user`) are only available from $onInit, not the constructor.
        $onInit(): void {
            if (!angular.isArray(this.user.phones)) {
                this.user.phones = [];
            }

            this.apiUserService.country().then((response: object) => {
                const json = response["data"];
                if (json.success) {
                    this.user.selectedCountry =
                        this.countryDataProvider.getCountryFromCode(
                            json.country
                        );
                }
            });
        }

        // Emoji flag from the country's ISO alpha-2 code (e.g. "IE" → 🇮🇪).
        flagEmoji(country: any): string {
            const code =
                country && country.code
                    ? String(country.code).toUpperCase()
                    : "";
            if (!/^[A-Z]{2}$/.test(code)) {
                return "";
            }
            const base = 0x1f1e6; // regional indicator "A"
            return String.fromCodePoint(
                base + code.charCodeAt(0) - 65,
                base + code.charCodeAt(1) - 65
            );
        }

        // Caret click: show the full country list. Clearing the search text
        // makes the autocomplete match everything, and focusing the input
        // (md-min-length="0") opens the dropdown.
        openCountryList(): void {
            this.user.countrySearchText = "";
            const input = this.$element[0].querySelector(
                ".invite-phone__code input"
            ) as HTMLInputElement | null;
            if (input) {
                input.focus();
            }
        }

        onDraftChange(): void {
            if (this.error) {
                this.error = "";
            }
        }

        inputKeydown(event: any): void {
            // Only Enter confirms a number (numbers can contain spaces/dashes,
            // so those are never separators).
            if (event.keyCode === this.$mdConstant.KEY_CODE.ENTER) {
                event.preventDefault();
                this.addPhone(true);
            }
        }

        // Light sanity check, not full validation (the country picker owns the
        // dial code and the API re-validates): formatting characters are fine,
        // but the number needs 4-14 digits (E.164 caps at 15 with the dial
        // code) and nothing alphabetic. Returns "" when acceptable.
        phoneNumberError(raw: string): string {
            const number = (raw || "").trim();
            if (number.charAt(0) === "+") {
                return "Enter the number without the country code — it is added automatically";
            }
            const digits = number.replace(/\D/g, "").length;
            if (
                !/^[\d\s().\/-]+$/.test(number) ||
                digits < 4 ||
                digits > 14
            ) {
                return "Enter a valid phone number";
            }
            return "";
        }

        // Adds the current number under the selected country code. The country
        // selection is intentionally left in place so the next number can be
        // added without re-picking it. showError surfaces validation (Enter).
        addPhone(showError: boolean): void {
            const number = (this.draftNumber || "").trim();
            if (number === "") {
                if (showError) {
                    this.error = "Enter a phone number";
                }
                return;
            }
            const numberError = this.phoneNumberError(number);
            if (numberError !== "") {
                if (showError) {
                    this.error = numberError;
                }
                return;
            }
            // selectCountryFromSearchText() returns the selected country when
            // one is set, and otherwise resolves it from the typed dial code —
            // so this single check covers both the picked and typed cases.
            if (!this.selectCountryFromSearchText()) {
                if (showError) {
                    this.error = "Choose a country code";
                }
                return;
            }
            const country = this.user.selectedCountry;
            const dialCode = country.dial_code;
            const entry = {
                number: number,
                countrycode: String(dialCode).replace(/^\+/, ""),
                dialCode: dialCode,
            };
            const exists = this.user.phones.some(
                (p: any) =>
                    p.number === entry.number &&
                    p.countrycode === entry.countrycode
            );
            if (!exists && this.user.phones.length < 1000) {
                this.user.phones.push(entry);
            }
            this.draftNumber = "";
            this.error = "";
        }

        removePhone(index: number): void {
            this.user.phones.splice(index, 1);
            this.error = "";
        }

        clearAll(): void {
            this.user.phones.splice(0, this.user.phones.length);
            this.error = "";
        }

        startEdit(index: number): void {
            this.editingIndex = index;
            this.editValue = this.user.phones[index].number;
            this.error = "";
        }

        editKeydown(event: any, index: number): void {
            if (event.keyCode === this.$mdConstant.KEY_CODE.ENTER) {
                event.preventDefault();
                this.commitEdit(index);
            } else if (event.keyCode === this.$mdConstant.KEY_CODE.ESCAPE) {
                this.cancelEdit();
            }
        }

        commitEdit(index: number): void {
            if (this.editingIndex !== index) {
                return;
            }
            const value = (this.editValue || "").trim();
            if (value === "") {
                this.user.phones.splice(index, 1);
            } else if (this.phoneNumberError(value) !== "") {
                // Invalid edit: keep the previous (valid) number and say why.
                this.error = this.phoneNumberError(value);
            } else {
                this.user.phones[index].number = value;
            }
            this.editingIndex = -1;
            this.editValue = "";
        }

        cancelEdit(): void {
            this.editingIndex = -1;
            this.editValue = "";
            this.error = "";
        }

        confirm(): void {
            // Fold in a number that was typed but not yet added.
            const pending = (this.draftNumber || "").trim();
            if (pending) {
                this.addPhone(true);
                if ((this.draftNumber || "").trim() !== "") {
                    // Still there → it was invalid; block advancing.
                    return;
                }
            }
            if (this.user.phones.length) {
                this.finishCallback({ stage: "contact" });
            } else {
                this.error = "Add at least one phone number";
            }
        }

        selectCountryFromSearchText(): object | boolean {
            if (this.user.selectedCountry) {
                return this.user.selectedCountry;
            }
            let foundCountry: any = false;
            angular.forEach(this.countries, (country: any) => {
                if (country.dial_code == this.user.countrySearchText) {
                    this.user.selectedCountry = country;
                    foundCountry = country;
                }
            });
            return foundCountry;
        }
    },
};

angular
    .module("invite")
    .component(InvitePhoneNumber.selector, InvitePhoneNumber);
